import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useAutoRefresh } from '../../utils/useAutoRefresh';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';
import { todayEasternDisplay, toEasternTime } from '../utils/time';

const SOURCE_LABELS = { comment: 'Comment', message: 'Message', lead: 'Lead', review: 'Review' };

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [posting, setPosting] = useState(false);
  const [showToast, toastNode] = useToast();

  const load = useCallback(async () => {
    try {
      setData(await api.getDashboard());
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load marketing data');
    }
  }, []);

  useAutoRefresh(load, 30000);

  const triggerPost = async () => {
    setPosting(true);
    try {
      await api.triggerPost();
      showToast('Post triggered. Check the Facebook Page in a moment.');
      await load();
    } catch (e) {
      showToast(e.message || 'Could not trigger a post', 'error');
    } finally {
      setPosting(false);
    }
  };

  const d = data || {};
  const escalations = (d.recent_escalations || []).slice(0, 5);
  const audit = d.recent_audit || [];

  return (
    <>
      <PageHeader
        title="Marketing"
        subtitle={todayEasternDisplay()}
        actions={(
          <>
            <button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>
            <button type="button" className="ops-btn ops-btn--primary" onClick={triggerPost} disabled={posting}>
              <Icon name="plus" /> {posting ? 'Posting...' : 'Post now'}
            </button>
          </>
        )}
      />

      {!data && error ? (
        <Panel>
          <Empty title="Marketing data unavailable">
            {error}
            <div style={{ marginTop: 12 }}><button type="button" className="ops-btn ops-btn--sm" onClick={load}>Try again</button></div>
          </Empty>
        </Panel>
      ) : !data ? (
        <Panel><Skeleton rows={5} /></Panel>
      ) : (
        <>
          <div className="ops-kpis ops-kpis--4">
            <StatCard label="Today's post" value={d.posted_today ? 'Done' : 'Not yet'} tone={d.posted_today ? 'green' : 'amber'} to="/marketing/posts" />
            <StatCard label="Open escalations" value={d.open_escalations ?? 0} tone={d.open_escalations > 0 ? 'red' : undefined} to="/marketing/escalations" />
            <StatCard label="New leads" value={d.new_leads ?? 0} tone={d.new_leads > 0 ? 'amber' : undefined} to="/marketing/leads" />
            <StatCard label="Pending comments" value={d.pending_comments ?? 0} to="/marketing/comments" />
          </div>

          <div className="ops-grid-2">
            <Panel title="Today's post" action={<Link to="/marketing/posts" className="ops-link">All posts</Link>}>
              {d.todays_post ? (
                <>
                  <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{d.todays_post.content}</p>
                  <StatusPill status={d.todays_post.status} />
                </>
              ) : (
                <Empty title="No post yet today">Use Post now to have the agent write and publish one.</Empty>
              )}
            </Panel>

            <Panel title="Open escalations" count={d.open_escalations ?? 0} flush action={<Link to="/marketing/escalations" className="ops-link">View all</Link>}>
              {escalations.length === 0 ? <Empty title="No open escalations">Nothing needs your attention.</Empty> : (
                <div className="ops-table-wrap">
                  <table className="ops-table ops-table--dash">
                    <tbody>
                      {escalations.map(e => (
                        <tr key={e.escalation_id}>
                          <td><StatusPill status={e.source_type} label={SOURCE_LABELS[e.source_type] || e.source_type} /></td>
                          <td className="neg" style={{ fontWeight: 500 }}>{e.classification}</td>
                          <td className="ops-cell--grow muted"><span className="ops-truncate">{e.content_summary}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <Panel title="Recent activity" count={audit.length} flush action={<Link to="/marketing/audit" className="ops-link">Full log</Link>}>
            {audit.length === 0 ? <Empty title="No activity yet today" /> : (
              <div className="ops-table-wrap">
                <table className="ops-table ops-table--dash">
                  <thead><tr><th>Time</th><th>Action</th><th>Result</th><th>Detail</th></tr></thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.log_id}>
                        <td className="mono muted">{toEasternTime(a.timestamp)}</td>
                        <td className="ops-cell-main">{a.action_type}</td>
                        <td><StatusPill status={a.success ? 'completed' : 'failed'} label={a.success ? 'OK' : 'Failed'} /></td>
                        <td className="ops-cell--grow muted"><span className="ops-truncate">{a.output_summary}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
      {toastNode}
    </>
  );
}
